<template>
  <error-boundary error-key="tariffScheduleGrid">
    <page-content-placeholder
      v-if="activeTariff?.remoteInteraction && activeAccount?.status === ContractStatus.OnApproval"
      max-width="640px"
      :img-src="TLPlaceholder"
    >
      <div class="caption">
        Цены будут загружены, после проверки вашего аккаунта модератором и изменения его статуса на
        <span class="text-green">«В&nbsp;работе»</span>
      </div>
    </page-content-placeholder>

    <page-content-placeholder
      v-else-if="activeAccount?.status === ContractStatus.InProgress && isEmptyGrid && activeTariff?.remoteInteraction"
      max-width="640px"
      :img-src="TLPlaceholder"
    >
      <div class="caption">
        Цены будут отражены после успешной синхронизации с менеджером каналов в течение нескольких минут
      </div>
    </page-content-placeholder>

    <template v-else>
      <e-alert v-if="isCorrected" class="mb-6" closable>
        <div class="paragraph-m_h font-600">Шахматка была исправлена</div>
        <div class="paragraph-s_h text-secondary mt-1">
          Даты с исправленными значениями для вашего удобства отмечены в шахматке обводкой
        </div>
      </e-alert>

      <e-alert variant="info" class="mb-6">
        <div class="paragraph-m_h font-600">
          {{
            isBruttoContractType
              ? 'Применяется схема взаиморасчетов - «Брутто тариф». '
              : 'Применяется схема взаиморасчётов «Нетто тариф».'
          }}
        </div>
        <div class="text-secondary paragraph-s_h alert-subtitle"
          v-if="
            activeTariff?.contractType === ContractType.Agency ||
            activeTariff?.contractType === ContractType.Sale ||
            activeTariff?.contractType === ContractType.SaleGross
          ">
          {{
            activeTariff?.contractType === ContractType.Agency
              ? 'Укажите цену с учётом агентского вознаграждения'
              : activeTariff?.contractType === ContractType.Sale
              ? 'Укажите цену с учётом скидки. Цену продажи регулирует Алеан'
              : activeTariff?.contractType === ContractType.SaleGross
              ? 'Укажите брутто тариф. К оплате — брутто тариф минус скидка 20%'
              : ''
          }}
        </div>
      </e-alert>

      <div class="d-flex mb-0 mb-md-6">
        <div class="w-100 mr-4">
          <prices-filters
            v-model="filters"
            :service-group-filter-items="serviceGroupFilterItems"
            :room-category-filter-items="shownRoomCategoriesFilters"
            :tourist-type-filter-items="touristTypeFilterItems"
            class="mb-6"
          />
        </div>
        <mobile-floating-bar>
          <operation-button
            variant="primary"
            :operation="tariffScheduleGrid?.operations?.edit"
            :loading="isEditGridStartLoading"
            @click="startGroupOperation"
          >
            Групповая операция
          </operation-button>
        </mobile-floating-bar>
      </div>

      <chess
        v-if="tariffScheduleGrid && chessItems"
        ref="pricesChessRef"
        accordion
        accordion-multiple
        :is-edit="isOperationEnabled(tariffScheduleGrid?.operations?.edit)"
        :period="tariffScheduleGrid.baseSettings.period"
        :items="chessItems"
        :chess-dates-available-from="getIsoDateWithUtcOffset(new Date(), timeZoneOffsetMin)"
        :loading="isLoading"
        :disable-loading="isEditGridStartLoading"
        @select="onChessSelection"
        dialog-title-type="full"
        :show-place-with-type-select="true"
      />

      <div v-if="$vuetify.display.xs" style="height: 72px" />
    </template>

    <edit-tariffs-dialog
      v-model="isGroupOperationDialogOpened"
      :selected-items="selectedItems"
      @close="onDialogClose"
    />
  </error-boundary>
</template>

<script setup lang="ts">
import dayjs from 'dayjs';
import { computed, ref, toRef } from 'vue';
import { useRoute } from 'vue-router';
import type { ChessComponent, ChessDateCellType, ChessItemType } from '@/core/components/interface/Chess/Chess.type';
import type { SimpleFilterItem } from '@/core/types/filters';
import TLPlaceholder from '@/assets/img/pages/tariffs/TL-grid-placeholder.svg';
import OperationButton from '@/core/components/buttons/OperationButton.vue';
import MobileFloatingBar from '@/core/components/common/MobileFloatingBar.vue';
import PageContentPlaceholder from '@/core/components/common/PageContentPlaceholder.vue';
import Chess from '@/core/components/interface/Chess/index.vue';
import { EAlert } from '@/core/components/ui';
import { useFetch } from '@/core/composables/useFetch';
import { getConfigValue } from '@/core/consts/config';
import { distinctArray } from '@/core/utils/array';
import { getIsoDateFormat, getIsoDateWithUtcOffset } from '@/core/utils/date';
import { formatPrice } from '@/core/utils/number';
import { isOperationEnabled } from '@/core/utils/permissions';
import { ContractStatus, ContractType } from '@/graphql/generated';
import ErrorBoundary from '@/parts/errors/components/ErrorBoundary.vue';
import { useAccommodationObjectsStore } from '@/parts/objects/store/accommodationObjects';
import EditTariffsDialog from '@/parts/tariffs/components/PricesAndRestrictions/EditTariffsDialog.vue';
import PricesFilters from '@/parts/tariffs/components/PricesAndRestrictions/PricesFilters.vue';
import { usePricesStore } from '@/parts/tariffs/store/prices';
import { useTariffsStore } from '@/parts/tariffs/store/tariffs';
import { touristPlaceName, touristTypeString } from '@/parts/tariffs/utils/tariffs';

const $route = useRoute();

const { activeAccount } = useAccommodationObjectsStore();
const { activeTariff } = useTariffsStore();

const { getTariffScheduleGrid, tariffScheduleGrid, editGridStart, isEditGridStartLoading } = usePricesStore();
const { timeZoneOffsetMin } = useAccommodationObjectsStore();

const pricesChessRef = ref<ChessComponent>();

const tariffId = toRef(() => String($route.params.t_id));

const isBruttoContractType = toRef(
  () => activeTariff.value?.contractType === ContractType.Agency || 
        activeTariff.value?.contractType === ContractType.SaleGross ||
        activeTariff.value?.contractType === ContractType.Booking
);

const grid = toRef(() => tariffScheduleGrid.value?.tabs?.grid);

const isEmptyGrid = toRef(() => !grid.value?.services?.length);

const isGroupOperationDialogOpened = ref(false);

const filters = ref({
  serviceGroupFilter: null,
  roomCategoryFilter: null,
  placeFilter: null,
  touristTypeFilter: null,
});

const serviceGroupFilterItems = toRef(
  () =>
    grid.value?.services?.map((service) => ({
      title: service.serviceGroup.name[0].value,
      value: service.serviceGroup.cid,
    })) || [],
);

const roomCategoriesFilterItems = toRef(
  () =>
    grid.value?.services?.reduce<
      (SimpleFilterItem & {
        touristTypes: string[];
      })[]
    >((res, service) => {
      res = [
        ...res,
        ...service.roomCategories.map((room) => ({
          title: `${room.roomCategory.type.name} ${room.roomCategory.name[0].value}`,
          value: room.roomCategory.cid,
          touristTypes: room.placingFactors.reduce<string[]>((res, placingFactor) => {
            const item = touristTypeString(placingFactor.placingFactor.touristPlace);

            if (!res.some((x) => x === item)) {
              res.push(item);
            }

            return res;
          }, []),
        })),
      ];

      return res;
    }, []) || [],
);

const shownRoomCategoriesFilters = toRef(() => distinctArray(roomCategoriesFilterItems.value, (x) => x.value));

const touristTypeFilterItems = toRef(() =>
  distinctArray(
    roomCategoriesFilterItems.value.reduce<string[]>((res, roomCategoryFilter) => {
      res = [...res, ...roomCategoryFilter.touristTypes];

      return res;
    }, []),
  ).filter((t) => t !== 'Весь номер'),
);

const startGroupOperation = async () => {
  const result = await editGridStart(tariffId.value);

  if (result?.data) {
    isGroupOperationDialogOpened.value = true;
  }
};

const selectedItems = ref<ChessDateCellType[]>();

const onChessSelection = (cells: ChessDateCellType[]) => {
  selectedItems.value = cells;

  if (cells.length) {
    void startGroupOperation();
  }
};

const onDialogClose = () => {
  pricesChessRef.value?.unselectAllCells();
};

const isCorrected = ref(false);
const correctedItems = ref<(string | number)[]>([]);

const chessItems = computed<ChessItemType[]>(() => {
  if (!grid.value?.services) {
    return [];
  }

  let filteredServices = grid.value.services;

  if (filters.value.serviceGroupFilter || filters.value.roomCategoryFilter) {
    filteredServices = filteredServices.filter(
      (service) =>
        (!filters.value.serviceGroupFilter || service.serviceGroup.cid === filters.value.serviceGroupFilter) &&
        (!filters.value.roomCategoryFilter ||
          service.roomCategories.some(
            (roomCategory) => roomCategory.roomCategory.cid === filters.value.roomCategoryFilter,
          )),
    );
  }

  const mappedItems: ChessItemType[] = filteredServices.map((service) => ({
    title: service.serviceGroup.name[0].value,
    tables: service.roomCategories
      .filter(
        (roomCategory) =>
          !filters.value.roomCategoryFilter || roomCategory.roomCategory.cid === filters.value.roomCategoryFilter,
      )
      .map((roomCategory) => ({
        fixed: {
          title: `${roomCategory.roomCategory.type.name} ${roomCategory.roomCategory.name[0].value}`,
          label: service.serviceGroup.name[0].value,
        },
        rows: roomCategory.placingFactors
          .filter(
            (placingFactor) =>
              (!filters.value.placeFilter ||
                (filters.value.placeFilter === 'whole' && !placingFactor.placingFactor.touristPlace) ||
                (placingFactor.placingFactor.touristPlace &&
                  !placingFactor.placingFactor.touristPlace.touristPlace.isAdditional &&
                  filters.value.placeFilter === 'base') ||
                (placingFactor.placingFactor.touristPlace?.touristPlace.isAdditional &&
                  filters.value.placeFilter === 'ext')) &&
              (!filters.value.touristTypeFilter ||
                touristTypeString(placingFactor.placingFactor.touristPlace) === filters.value.touristTypeFilter),
          )
          .map((placingFactor, index) => ({
            fixed: {
              title: !placingFactor.placingFactor.touristPlace
                ? placingFactor.placingFactor.roomPlacing?.placingVariant.name
                : touristPlaceName(placingFactor.placingFactor.touristPlace.touristPlace.isAdditional),
              text: touristTypeString(placingFactor.placingFactor.touristPlace),
              tooltip: placingFactor.placingFactor.touristPlace?.relatedRoomPlacings?.reduce(
                (res, item) => (res += `${item} <br>`),
                '',
              ),
            },
            cells: placingFactor.rates.reduce<Record<string, ChessDateCellType>>((res, rate) => {
              let date = dayjs(rate.period.from);
              const endDate = dayjs(rate.period.to);

              while (date <= endDate) {
                const formattedDate = getIsoDateFormat(date);
                const id = `${service.serviceGroup.cid}/${roomCategory.roomCategory.cid}/placing_${index}/date_${+date}`;
                res[formattedDate] = {
                  date: formattedDate,
                  content: `<div style="text-align: right">${!rate.price ? '–' : formatPrice(rate.price.sale, 0)}</div>`,
                  id,
                  variant: correctedItems.value.includes(id) ? 'highlight' : undefined,
                };

                date = dayjs(date).add(1, 'days');
              }

              return res;
            }, {}),
          })),
      })),
  }));

  return mappedItems.reduce<ChessItemType[]>((res, item) => {
    item.tables = item.tables.filter((tableItem) => tableItem.rows.length);

    if (!item.tables.length) {
      return res;
    }

    res.push(item);

    return res;
  }, []);
});

const { isLoading } = useFetch(async () => {
  await getTariffScheduleGrid(tariffId.value);
});
</script>

<style scoped lang="scss">
.alert-subtitle {
  margin-top: 2px;
}
</style>